import React, { useEffect, useRef, useState } from 'react';
import { IoMic, IoMicOff, IoVideocam, IoVideocamOff, IoPeople, IoPersonOutline } from 'react-icons/io5';
import logo from '../../images/icon_small.png';
import { imageSrc } from '../../utils/avatar';
import { initialsOf, avatarColor } from '../../utils/meetingAvatar';
import { useLanguage } from '../../i18n/LanguageContext';

const avatarUrl = (imageUrl) => imageSrc(imageUrl);

// "X, Y ve 2 kişi daha toplantıda" - avatars for the first few, a count for
// the rest, so arriving at a meeting that is already underway feels different
// from arriving at an empty one.
const MAX_FACES = 3;

function AlreadyInside({ participants }) {
    const { t } = useLanguage();
    if (participants.length === 0) {
        return <p className="MeetLobbyInside MeetLobbyInsideEmpty">{t('meeting.lobby.nobodyYet')}</p>;
    }
    const faces = participants.slice(0, MAX_FACES);
    const overflow = participants.length - faces.length;
    const names = faces.map((p) => p.name).join(', ');

    return (
        <div className="MeetLobbyInside">
            <div className="MeetLobbyFaces">
                {faces.map((p, index) => (
                    <span
                        key={`${p.name}-${index}`}
                        className="MeetLobbyFace"
                        style={p.imageUrl ? undefined : { background: avatarColor(p.name) }}
                        title={p.name}
                    >
                        {p.imageUrl
                            ? <img src={avatarUrl(p.imageUrl)} alt={p.name} />
                            : initialsOf(p.name)}
                    </span>
                ))}
            </div>
            <span className="MeetLobbyInsideText">
                {overflow > 0
                    ? t('meeting.lobby.insideWithMore', { names, count: overflow })
                    : t('meeting.lobby.inside', { names, count: participants.length })}
            </span>
        </div>
    );
}

// The pre-join screen. Two jobs: let people choose how they arrive (name,
// camera, mic) and tell them what they are walking into (title, host, who is
// already there). Everything here runs against raw getUserMedia rather than a
// LiveKit connection - nobody should appear in the call, even silently, before
// they press join.
export default function MeetingLobby({
    meeting, participants, defaultName, nameLocked,
    micOn, cameraOn, onToggleMic, onToggleCamera,
    onJoin, joining, error
}) {
    const { t } = useLanguage();
    const [name, setName] = useState(defaultName || '');
    const [previewStream, setPreviewStream] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const videoRef = useRef(null);

    // The preview camera is opened and closed by `cameraOn` alone, so turning
    // it off here really releases the device (the OS indicator light goes out)
    // instead of just hiding a still-running stream.
    useEffect(() => {
        if (!cameraOn) return undefined;
        let cancelled = false;
        let stream = null;
        navigator.mediaDevices.getUserMedia({ video: true })
            .then((result) => {
                if (cancelled) { result.getTracks().forEach((track) => track.stop()); return; }
                stream = result;
                setPreviewStream(result);
                setPreviewError(null);
            })
            .catch(() => {
                if (!cancelled) {
                    setPreviewError(t('meeting.lobby.cameraBlocked'));
                    // Reflect reality in the button rather than leaving it lit
                    // for a camera that never opened.
                    onToggleCamera(false);
                }
            });
        return () => {
            cancelled = true;
            if (stream) stream.getTracks().forEach((track) => track.stop());
            setPreviewStream(null);
        };
        // onToggleCamera/t are stable enough in practice and including them
        // would re-open the camera on every render of the parent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraOn]);

    useEffect(() => {
        if (videoRef.current) videoRef.current.srcObject = previewStream;
    }, [previewStream]);

    const trimmedName = name.trim();
    const canJoin = !joining && (nameLocked || trimmedName.length >= 2);

    const submit = (event) => {
        event.preventDefault();
        if (!canJoin) return;
        // The preview's own camera track is stopped by the effect cleanup when
        // this component unmounts; LiveKit opens its own afterwards.
        onJoin(nameLocked ? defaultName : trimmedName);
    };

    return (
        <div className="parallax-back MeetLobby">
            <header className="MeetLobbyBrand">
                <img src={logo} alt="Cyclone" className="BrandFallbackImg" />
                <span>Cyclone</span>
                {/* The group this meeting belongs to, beside the Cyclone mark -
                    the same pairing the in-call top bar shows. */}
                {meeting.groupImageUrl && (
                    <>
                        <span className="MeetLobbyBrandSep" />
                        <img className="MeetLobbyGroupLogo" src={avatarUrl(meeting.groupImageUrl)} alt={meeting.groupName || ''} />
                    </>
                )}
                {meeting.groupName && <span className="MeetLobbyGroupName">{meeting.groupName}</span>}
            </header>

            <div className="MeetLobbyBody">
                <div className="MeetLobbyPreview">
                    <div className="MeetLobbyVideoWrap">
                        {cameraOn && previewStream ? (
                            // Mirrored (see .MeetLobbyVideo in Meeting.css): a
                            // self-preview that isn't mirrored reads as someone
                            // else's camera, not your own.
                            <video ref={videoRef} className="MeetLobbyVideo" autoPlay playsInline muted />
                        ) : (
                            <div className="MeetLobbyVideoOff">
                                {/* initialsOf falls back to "?" for an empty
                                    name, which reads as an error rather than
                                    as "you have not typed one yet". */}
                                <span className="MeetLobbyInitials">
                                    {(nameLocked ? defaultName : trimmedName) ? initialsOf(nameLocked ? defaultName : trimmedName) : <IoPersonOutline />}
                                </span>
                                <span className="MeetLobbyVideoOffText">
                                    {previewError || t('meeting.lobby.cameraOff')}
                                </span>
                            </div>
                        )}
                        <div className="MeetLobbyPreviewControls">
                            <button
                                type="button"
                                onClick={() => onToggleMic(!micOn)}
                                className={`MeetRoundBtn ${micOn ? '' : 'MeetRoundBtnDanger'}`}
                                title={micOn ? t('meeting.micOff') : t('meeting.micOn')}
                            >
                                {micOn ? <IoMic /> : <IoMicOff />}
                            </button>
                            <button
                                type="button"
                                onClick={() => onToggleCamera(!cameraOn)}
                                className={`MeetRoundBtn ${cameraOn ? '' : 'MeetRoundBtnDanger'}`}
                                title={cameraOn ? t('meeting.cameraOff') : t('meeting.cameraOn')}
                            >
                                {cameraOn ? <IoVideocam /> : <IoVideocamOff />}
                            </button>
                        </div>
                    </div>
                    <p className="MeetLobbyHint">{t('meeting.lobby.hint')}</p>
                </div>

                <form className="MeetLobbyJoin" onSubmit={submit}>
                    <h1 className="MeetLobbyTitle">{meeting.title}</h1>
                    <p className="MeetLobbyHost">{t('meeting.lobby.hostedBy', { name: meeting.hostName })}</p>

                    <div className="MeetLobbyInsideBlock">
                        <span className="MeetLobbyInsideLabel"><IoPeople /> {t('meeting.lobby.whoIsInside')}</span>
                        <AlreadyInside participants={participants} />
                    </div>

                    {!nameLocked && (
                        <label className="MeetLobbyField">
                            <span>{t('meeting.lobby.yourName')}</span>
                            <input
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                maxLength={32}
                                placeholder={t('meeting.lobby.namePlaceholder')}
                                autoFocus
                            />
                        </label>
                    )}
                    {nameLocked && <p className="MeetLobbyAs">{t('meeting.lobby.joiningAs', { name: defaultName })}</p>}

                    {error && <p className="MeetLobbyError">{error}</p>}

                    <button type="submit" className="MeetLobbyJoinBtn primaryColor" disabled={!canJoin}>
                        {joining ? t('meeting.lobby.joining') : t('meeting.lobby.join')}
                    </button>
                    <p className="MeetLobbyCode">{t('meeting.lobby.code', { code: meeting.code })}</p>
                </form>
            </div>
        </div>
    );
}
